import {
    Diagnostic,
    DiagnosticLocation,
    DiagnosticOrigin,
    DiagnosticSeverity,
    REPORT_DIAGNOSTIC,
    FINALIZE_PARSE,
    CLEAR_DIAGNOSTICS,
    CLEAR_DIAGNOSTICS_FOR_FILE,
    ReportDiagnosticAction,
    FinalizeParseAction,
    ClearDiagnosticsAction,
    ClearDiagnosticsForFileAction,
} from "./types";
import { sendDiagnosticsToVsCode } from "../vscode/actions";
import type { JsxParserError, SourceInfo } from "@recursive-robot/react-jsx-parser";

/**
 * The structured source information attached to a diagnostic.  Mirrors the `sourceInfo`
 * field of the JSX parser's error type so that future (non-parser) producers can reuse it.
 */
export type DiagnosticSourceInfo = {
    fileName?: string;
    source?: string;
    location?: DiagnosticLocation;
    loopIndex?: number;
};

function buildDiagnosticId(origin: DiagnosticOrigin, type: string, location: DiagnosticLocation, loopIndex?: number): string {
    return `${origin}:${type}@${location.line}:${location.column ?? ""}#${loopIndex ?? ""}`;
}

/**
 * Shared core action used by every diagnostic producer.  Records the diagnostic and pushes
 * the current diagnostic set to the VS Code extension / LSP (a no-op when disconnected).
 */
export function reportDiagnostic(diagnostic: Diagnostic, bucketKey: string, fileName: string | undefined, source: string, parseId: string) {
    return (dispatch) => {
        dispatch({
            type: REPORT_DIAGNOSTIC,
            bucketKey,
            fileName,
            source,
            parseId,
            diagnostic
        } as ReportDiagnosticAction);
        dispatch(sendDiagnosticsToVsCode());
    };
}

/**
 * Maps a JSX parser error (`JsxParserError`, or a plain `Error` fallback) into a `Diagnostic`.
 *
 * @param fileName - The template file name, if known (`undefined` for inline templates).
 * @param source - The template source being parsed; used as the parse identity (parseId).
 * @param error - The error raised by the JSX parser's `onError` callback.
 */
export function reportParserError(fileName: string | undefined, source: string | undefined, error: JsxParserError | Error) {
    return (dispatch) => {
        const info = (error as JsxParserError)?.sourceInfo ?? {} as SourceInfo;
        const location: DiagnosticLocation = info.location ?? { line: 0 };
        const loopIndex: number | undefined = info.loopIndex;
        const type: string = (error as JsxParserError)?.type ?? "error";
        const origin: DiagnosticOrigin = "parser";

        // The prop `source` is the canonical parse identity (the JSX parser re-parses when it changes).
        // Fall back to the parser-reported source only if the prop is missing.
        const canonicalSource = source ?? info.source ?? "";
        const bucketKey = fileName ?? canonicalSource;

        const diagnostic: Diagnostic = {
            id: buildDiagnosticId(origin, type, location, loopIndex),
            origin,
            type,
            severity: "error",
            message: error?.message ?? String(error),
            cause: error?.cause && String(error.cause),
            location,
            loopIndex,
            snippet: (error as JsxParserError)?.snippet
        };

        dispatch(reportDiagnostic(diagnostic, bucketKey, fileName, canonicalSource, canonicalSource));
    };
}

/**
 * Maps a component-level input-validation error into a `Diagnostic`.  Validation errors carry
 * the same `sourceInfo` shape as parser errors, so they share the bucket keying and lifecycle.
 */
export function reportValidationError(
    fileName: string | undefined,
    sourceInfo: DiagnosticSourceInfo,
    details: { type: string; message: string; componentName?: string; severity?: DiagnosticSeverity }
) {
    return (dispatch) => {
        const location: DiagnosticLocation = sourceInfo?.location ?? { line: 0 };
        const loopIndex = sourceInfo?.loopIndex;
        const origin: DiagnosticOrigin = "validation";
        const canonicalSource = sourceInfo?.source ?? "";
        const bucketKey = fileName ?? canonicalSource;

        const diagnostic: Diagnostic = {
            id: buildDiagnosticId(origin, details.type, location, loopIndex),
            origin,
            type: details.type,
            severity: details.severity ?? "error",
            message: details.message,
            location,
            loopIndex,
            componentName: details.componentName
        };

        dispatch(reportDiagnostic(diagnostic, bucketKey, fileName, canonicalSource, canonicalSource));
    };
}

/**
 * Marks a parse pass complete.  If the bucket's recorded parse predates this one (the content
 * changed and produced no diagnostics), its stale diagnostics are cleared.
 */
export function finalizeParse(bucketKey: string, parseId: string) {
    return (dispatch) => {
        dispatch({ type: FINALIZE_PARSE, bucketKey, parseId } as FinalizeParseAction);
        dispatch(sendDiagnosticsToVsCode());
    };
}

export function clearDiagnostics() {
    return (dispatch) => {
        dispatch({ type: CLEAR_DIAGNOSTICS } as ClearDiagnosticsAction);
        dispatch(sendDiagnosticsToVsCode());
    };
}

export function clearDiagnosticsForFile(fileName: string, diagnosticId?: string) {
    return (dispatch) => {
        dispatch({ type: CLEAR_DIAGNOSTICS_FOR_FILE, fileName, diagnosticId } as ClearDiagnosticsForFileAction);
        dispatch(sendDiagnosticsToVsCode());
    };
}
